import React, { useState, useEffect, useRef } from 'react';
import {
  SliderContainer,
  SliderWrapper,
  Slide,
  DotsContainer,
  Dot,
} from './Slider.styled';

interface SliderProps {
  content: React.ReactNode[];
  dots?: boolean;
  infinite?: boolean;
  speed?: number;
  slidesToShow?: number;
  dotsClass?: string;
  initialSlide?: number;
  onSlideChange?: (index: number) => void;
}

export function Slider ({
  content,
  dots = true,
  infinite = true,
  speed = 500,
  slidesToShow = 1,
  dotsClass,
  initialSlide = 0,
  onSlideChange,
}: SliderProps) {
  const [currentSlide, setCurrentSlide] = useState<number>(initialSlide);
  const [translateX, setTranslateX] = useState<number>(0);
  const slideWidth = 100 / slidesToShow;
  const totalSlides = content.length;
  const containerRef = useRef<HTMLDivElement>(null);
  const touchStartX = useRef(0);
  const touchEndX = useRef(0);

  const goToSlide = (index: number) => {
    if (totalSlides === 0) {
      return;
    }

    if (infinite) {
      const normalizedIndex =
        ((index % totalSlides) + totalSlides) % totalSlides;
      setCurrentSlide(normalizedIndex);
      setTranslateX(-normalizedIndex * slideWidth);
      if (onSlideChange) {
        onSlideChange(normalizedIndex);
      }
    } else {
      const clampedIndex = Math.max(0, Math.min(index, totalSlides - 1));
      setCurrentSlide(clampedIndex);
      setTranslateX(-clampedIndex * slideWidth);
      if (onSlideChange) {
        onSlideChange(clampedIndex);
      }
    }
  };

  useEffect(() => {
    if (totalSlides === 0) {
      return;
    }

    if (infinite) {
      const normalizedIndex = currentSlide % totalSlides;
      setTranslateX(-normalizedIndex * slideWidth);
    } else {
      const clampedIndex = Math.max(0, Math.min(currentSlide, totalSlides - 1));
      setTranslateX(-clampedIndex * slideWidth);
    }
  }, [currentSlide, slideWidth, infinite, totalSlides]);

  useEffect(() => {
    goToSlide(initialSlide);
  }, [initialSlide]);

  const handleDotClick = (index: number) => {
    goToSlide(index);
  };

  const handleTouchStart = (touchEvent: React.TouchEvent) => {
    touchStartX.current = touchEvent.touches[0].clientX;
    touchEndX.current = touchEvent.touches[0].clientX;
    touchEvent.preventDefault();
  };

  const handleTouchMove = (touchEvent: React.TouchEvent) => {
    if (Math.abs(touchEvent.touches[0].clientX - touchStartX.current) > 10) {
      touchEvent.preventDefault();
    }
  };

  const handleTouchEnd = (e: React.TouchEvent) => {
    touchEndX.current = e.changedTouches[0].clientX;
    handleSwipe();
    e.preventDefault();
  };

  const handleSwipe = () => {
    if (totalSlides === 0) {
      return;
    }

    const diff = touchStartX.current - touchEndX.current;
    const minSwipeDistance = 50;

    if (Math.abs(diff) > minSwipeDistance) {
      if (diff > 0) {
        if (infinite || currentSlide < totalSlides - 1) {
          goToSlide(currentSlide + 1);
        }
      } else {
        if (infinite || currentSlide > 0) {
          goToSlide(currentSlide - 1);
        }
      }
    }
  };

  return (
    <SliderContainer
      ref={containerRef}
      onTouchStart={handleTouchStart}
      onTouchMove={handleTouchMove}
      onTouchEnd={handleTouchEnd}
    >
      <SliderWrapper translateX={translateX} transitionSpeed={speed}>
        {content.map((child, index) => (
          <Slide key={index} width={slideWidth}>
            {child}
          </Slide>
        ))}
      </SliderWrapper>
      {dots && (
        <DotsContainer dotsClass={dotsClass}>
          {content.map((_, index) => (
            <Dot
              key={index}
              isActive={index === currentSlide}
              onClick={() => handleDotClick(index)}
              aria-label={`Go to slide ${index + 1}`}
            />
          ))}
        </DotsContainer>
      )}
    </SliderContainer>
  );
}
